import React, { useState, useEffect, useMemo, useRef } from 'react';
import { bindActionCreators } from 'redux';
import { connect, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import CardHead from 'components/commons/CardHead';
import { Button, Checkbox, Col, DatePicker, Row, Select, Space, Spin, Table, Tooltip, Switch } from 'antd';
import moment from 'moment';
import { PAGE_TYPE_CONVERT } from 'utils/constants';
import groupBy from 'lodash/collection/groupBy';
import { useTranslation } from 'react-i18next';
import { countryI18n, CountryUtil, countryFlagUrl } from 'utils/country';
import { formatEnNumber } from 'utils/format/format-number';
import { useZhEn } from 'utils/react-hooks/useZhEn';
import XLSX from 'xlsx';
import { getSummaryTags } from 'utils/StringUtils';
import {
  selectCurrentAppCustomUserSceneTagLabel,
  selectCurrentAppDatePickerAvailableMomentRange
} from 'utils/selectors/selectors';
import reportEvent, { EVENT_ACTIONS } from 'utils/reportEvent';
import CountryIcon from 'svg/v2/newcs_dashboard_overview_statsbyregion_icon.svg';
import CsDownloadButton from 'components/commons/CsDownloadButton/CsDownloadButton';
import CsCollapseButton from 'components/commons/CsCollapseButton/CsCollapseButton';
import WrappedTipsIcon from 'components/antd-extension/WrappedTipsIcon.jsx';
import _style from './StatByCountry.scss';
import { UpdateTimeTag } from './UpdateTimeTag';
import { DashboardConst } from 'utils/constants/dashboard-const';
import UserSceneTagSelect from 'components/exception/ProductSummaryPage/UserSceneTagSelect';
import VersionHybridSelect from 'components/commons/VersionHybridSelect';
import { VersionUtil } from 'utils/version-util';
import { orange } from '@ant-design/colors';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { PlatformUtil } from 'utils/platform';
import VmTypeSelect from 'components/exception/ProductSummaryPage/VmTypeSelect';
import { VmTypeInt } from 'utils/constants/vm-type';
import { ServerAppSettings } from 'utils/server-app-settings';
import VitalOrCrashSelect from 'components/exception/ProductSummaryPage/VitalOrCrashSelect';
import { BillingTeamType } from 'utils/constants/billing';
import { ExceptionCategoryUtil } from 'utils/exception-category';
import { CsvUtil } from 'utils/csv-util';

const { RangePicker } = DatePicker;

// 和平定制需求：联网设备数最小值过滤选项
const MIN_ACCESS_DEVICE_OPTIONS = [
  { label: '0', value: 0 },
  { label: '100', value: 100 },
  { label: '1000', value: 1000 },
  { label: '10000', value: 10000 },
];

const StatByUserSceneTag = (props) => {
  const { t } = useTranslation();
  const { ze } = useZhEn();

  const [
    datePickerMinAvailableMoment,
    datePickerMaxAvailableMoment,
  ] = useSelector(selectCurrentAppDatePickerAvailableMomentRange);

  const customUserSceneTagLabel = useSelector(selectCurrentAppCustomUserSceneTagLabel);

  const {
    reduxState,
    forceFetchTrigger,
    updateSearchParams,
  } = props;
  const currentApp = reduxState.app.get('current').toJS();
  const bigDataRealtimeUpdateMillis = reduxState.summary.get('bigDataRealtimeUpdateMillis');
  const { appId, platformId, appName, teamType } = currentApp;
  // 用细粒度 getIn() 替代 summary.toJS() 全量深拷贝，保留 Immutable 结构性共享以减少无效重渲染开销
  const pageType = reduxState.summary.getIn(['selectOptions', 'pageType']);
  const subModuleId = reduxState.summary.getIn(['searchParams', 'subModuleId']);
  const pid = reduxState.summary.getIn(['searchParams', 'pid']);
  const initialTrendVersion = reduxState.summary.getIn(['searchParams', 'trendVersion']);
  const reportCategory = PAGE_TYPE_CONVERT[pageType];
  // 直接用 reducer 里已经构造好的 versionTree（已经处理过版本号 encode 显示问题），
  // 不要在这里用 rankVersion.options 重新构造一遍，避免漏掉 encode 展示处理
  // 注意：这里必须 .toJS()——summaryReducer 里是用 `options.merge({ rankVersion: {...} })` 合并的，
  // merge 的参数是一个普通 JS 对象（不是 Immutable Collection），immutable.js 在这种情况下会对
  // 合并进来的每个顶层 value 整体做一次 fromJS 深度转换，所以 state 里 rankVersion.versionTree 实际
  // 存的是 Immutable List/Map，并不是原始的普通数组。不加 .toJS() 的话，TreeSelect 用
  // node.value / node.title 取不到值（Immutable Map 不支持这种属性访问），下拉框里所有节点都会
  // 显示成占位符（也就是这次看到的全部是 "---"）。
  // .toJS() 是深拷贝，成本和版本树规模成正比，这里用 useMemo 包一层：只有当 immutable 引用本身变化时
  // （也就是 versionTree 真的更新了，得益于 immutable.js 的结构共享）才会重新执行 toJS()，避免每次
  // 渲染都重复深拷贝。
  const versionTreeImmutable = reduxState.summary.getIn(['selectOptions', 'rankVersion', 'versionTree']);
  const versionTree = useMemo(() => versionTreeImmutable?.toJS() ?? [], [versionTreeImmutable]);

  // 元梦定制需求：元梦项目需要额外展示"联网用户数"(accessPeople)列，区别于"联网设备数"(accessUser)
  const isYuanmengApp = teamType === BillingTeamType.TENCENT && appName?.startsWith('元梦');

  // 和平定制需求：和平项目需要特殊的版本同步、默认排序、联网设备数过滤逻辑
  const isHepingApp = teamType === BillingTeamType.TENCENT && appName?.startsWith('和平');
  // 和平正式服：联网设备数过滤仅对正式服生效
  const isHepingProdApp = isHepingApp && appName?.includes('正式');
  // 当前是否 OOM 页面（仅 OOM 页面才应用"按次数 OOM 率倒序"的默认排序）
  const isOomPage = ExceptionCategoryUtil.isOom(pageType);
  // 和平 + OOM 页面：默认按次数 OOM 率倒序排序
  const shouldDefaultSortByNumOomRate = isHepingApp && isOomPage;

  // 从 redux 中同步的版本值（和平项目使用）
  const trendVersionListValue = useMemo(() => {
    return initialTrendVersion && initialTrendVersion !== '-1'
      ? CsvUtil.singleLineParse(initialTrendVersion)
      : [];
  }, [initialTrendVersion]);

  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countryList, setCountryList] = useState([]);
  const [userSceneTagList, setUserSceneTagList] = useState([]);
  // 和平项目：初始版本从 redux trendVersion 同步；非和平项目：保持原来的空数组行为
  const [versionList, setVersionList] = useState(isHepingApp ? trendVersionListValue : []);
  // 最新的本地版本列表 ref，供异步回调中读取最新值
  const latestVersionListRef = useRef(versionList);
  latestVersionListRef.current = versionList;
  // 防止本组件主动 updateSearchParams 后，trendVersion 变化 useEffect 再触发一次搜索
  const justSyncedVersionRef = useRef(false);
  const [vmTypeList, setVmTypeList] = useState([VmTypeInt.ALL]);
  const [minDate, setMinDate] = useState(moment().format('YYYY-MM-DD'));
  const [maxDate, setMaxDate] = useState(moment().format('YYYY-MM-DD'));
  const [countryStatList, setCountryStatList] = useState([]);
  const [isMultiDayResult, setIsMultiDayResult] = useState(false);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  const [isDisplayMap, setIsDisplayMap] = useState(false);
  const [currentTab, setCurrentTab] = useState('EXCP_OVERVIEW.occurNumber');
  const [tabs, setTabs] = useState([]);
  const [tableDom, setTableDom] = useState();

  // 和平定制需求：联网设备数最小值过滤，仅和平正式服生效，默认100
  const [minAccessDevice, setMinAccessDevice] = useState(isHepingProdApp ? 100 : 0);

  const shouldShowAccessPeople = isYuanmengApp;

  // 和平定制需求：根据联网设备数最小值过滤后的数据源
  const filteredCountryStatList = useMemo(() => {
    if (!minAccessDevice || minAccessDevice <= 0) return countryStatList;
    return countryStatList.filter(item => (item.accessUser || 0) >= minAccessDevice);
  }, [countryStatList, minAccessDevice]);

  const tabsMap = {
    'EXCP_OVERVIEW.userCrashRate': 'devicePercentage',
    'EXCP_OVERVIEW.occurNumCrashRate': 'numPercentage',
    'EXCP_OVERVIEW.occurNumber': 'crashNum',
    'EXCP_OVERVIEW.userNumAffect': 'crashUser',
    'EXCP_OVERVIEW.sessionCount': 'accessNum',
    'EXCP_OVERVIEW.userNumConnect': 'accessUser',
    'EXCP_OVERVIEW.userANRRate': 'devicePercentage',
    'EXCP_OVERVIEW.occurNumANRRate': 'numPercentage',
    'EXCP_OVERVIEW.userErrorRate': 'devicePercentage',
    'EXCP_OVERVIEW.occurNumErrorRate': 'numPercentage',
    'EXCP_OVERVIEW.sessionCrashRate': 'sessionPercentage',
    'EXCP_OVERVIEW.sessionNumAffect': 'crashSession',
  };
  const notDisplayMutilDay = ['EXCP_OVERVIEW.userCrashRate', 'EXCP_OVERVIEW.userANRRate', 'EXCP_OVERVIEW.userErrorRate', 'EXCP_OVERVIEW.userNumAffect', 'EXCP_OVERVIEW.userNumConnect'];

  // 切换pageType时设置对应的tabs数组
  useEffect(() => {
    const tabs = getSummaryTags(pageType, pid);
    setTabs(tabs);
  }, [pid, pageType]);

  // 切换app时重置搜索字段
  useEffect(() => {
    if (!appId) {
      return;
    }
    const serverAppSettings = reduxState.app.get('appIdToServerAppSettings')[appId] || {};
    const dashboardDefaultVmType = serverAppSettings[ServerAppSettings.keys.dashboardDefaultVmType] || VmTypeInt.ALL;
    setVmTypeList([dashboardDefaultVmType]);
    setCountryList([]);
    // 和平项目：切换 app 后保持与 redux trendVersion 同步，避免重置成空再被同步 useEffect 覆盖
    setVersionList(isHepingApp ? trendVersionListValue : []);
    setMinDate(moment().format('YYYY-MM-DD'));
    setMaxDate(moment().format('YYYY-MM-DD'));
    setFetchTrigger(fetchTrigger + 1);
  }, [appId]);

  // 和平定制需求：外部 trendVersion 变化时（趋势图/ModelOomTable等），同步本地版本并触发搜索
  useEffect(() => {
    if (!isHepingApp || !appId) {
      return;
    }
    // 跳过本组件自身 handleQuerySubmit 触发的 trendVersion 变化，避免重复搜索
    if (justSyncedVersionRef.current) {
      justSyncedVersionRef.current = false;
      return;
    }
    setVersionList(trendVersionListValue);
    latestVersionListRef.current = trendVersionListValue;
    setFetchTrigger(prev => prev + 1);
  }, [initialTrendVersion]);
  useEffect(() => {
    if (!appId) {
      return;
    }
    setFetchTrigger(fetchTrigger + 1);
  }, [pageType]);

  useEffect(() => {
    if (!fetchTrigger) {
      return;
    }
    fetchTrend();
  }, [fetchTrigger]);

  useEffect(() => {
    if (!forceFetchTrigger) {
      return;
    }
    fetchTrend();
  }, [forceFetchTrigger]);

  // 当前tab状态变化时切换对应的tableDOM
  useEffect(() => {
    if (!isDisplayMap) {
      setTableDom(allColumnsTableDom);
      return;
    }
    setTableDom(tablesArr.filter(table => table.key === currentTab)[0]);
    // 选择多日数据时，如果当前tab仍在不可展示项则切换至'发生次数'tab
    if (isMultiDayResult && notDisplayMutilDay.includes(currentTab)) {
      setCurrentTab('EXCP_OVERVIEW.occurNumber');
    }
  }, [currentTab, isDisplayMap, countryStatList, isMultiDayResult]);

  async function fetchTrend() {
    setLoading(true);

    const startDate = minDate.replace(/-/g, '');
    const endDate = maxDate.replace(/-/g, '');
    const rsp = await RestHelper.mustPost('/api/app/getTrend', {
      appId,
      platformId,
      subModuleId,
      versionList: versionList,
      mergeMultipleVersionsWithInaccurateResult: true,
      startDate,
      endDate,
      type: reportCategory,
      vmTypeList,
      mergeVmTypes: true,
      needUserSceneTagDimension: true,
      userSceneTagList,
      needPeopleMetrics: true,
    });
    const trendDataList = CountryUtil.convertTrendCountryGroupIdToName(reduxState.summary, rsp.json.ret.data);
    // 多个日期的数据合并（GET_TREND_SUCC回调那边有类似逻辑，后面可以抽取成公共的）
    const keyToDateTrendList = groupBy(trendDataList, x => x.userSceneTag);
    const countryStatList = Object.entries(keyToDateTrendList).map(([country, dataTrendList]) => dataTrendList.reduce((acc, x) => {
      if (!acc) {
        return x;
      }
      return {
        ...acc,
        accessNum: x.accessNum + acc.accessNum,
        accessUser: x.accessUser + acc.accessUser,
        accessPeople: (x.accessPeople || 0) + (acc.accessPeople || 0), // 联网用户数，由needPeopleMetrics参数控制返回
        crashNum: x.crashNum + acc.crashNum,
        crashSession: x.crashSession + acc.crashSession,
        crashUser: x.crashUser + acc.crashUser,
      };
    }, null));
    const enrichedCountryStatList = countryStatList
      .map((x) => {
        const devicePercentage = x.accessUser > 0
          ? (100 * x.crashUser / x.accessUser)
          : 0;
        const numPercentage = x.accessNum > 0
          ? (100 * x.crashNum / x.accessNum)
          : 0;
        const sessionPercentage = x.accessNum > 0
          ? (100 * x.crashSession / x.accessNum)
          : 0;
        return {
          ...x,
          devicePercentage,
          sessionPercentage,
          numPercentage,
        };
      })
      .filter(x => x.userSceneTag !== DashboardConst.USER_SCENE_TAG_ALL) // 去掉全量数据
      .filter(x => x.country !== '-');
    setCountryStatList(enrichedCountryStatList);
    if (countryStatList.length && !isExpanded) {
      setIsExpanded(true);
    }
    setIsMultiDayResult(startDate !== endDate);

    setLoading(false);
  }

  const multiDayNotSupportedDom = useMemo(() => <Tooltip
    title={ze(
      '多日查询不支持设备数相关指标（影响设备数、联网设备数等）',
      'If the selected time range spans multiple days and performance considerations are taken into account, some device-related metric queries may not be supported, such as "Affected devices" or "Active devices".',
    )}>
    <Space size={4}>
      <div>-</div>
      <WrappedTipsIcon />
    </Space>
  </Tooltip>, [ze]);

  const isMultipleVersions = VersionUtil.isMultipleVersions(versionList);

  // 渲染列表的国家地区label，带上国旗图标和中英文名称
  const renderCountryLabelDom = (country, type) => {
    if (type) {
      return !CountryUtil.isDeprecated(country) ? `${countryI18n(country)}` : `${countryI18n(country)} ${ze('(已废弃)', '(Deprecated)')}`;
    }
    return <span style={{ display: 'flex', alignItems: 'center', height: 16 }}>
      {countryFlagUrl(country)
        ? (<img style={{ width: '20px', marginRight: '5px' }} alt="Icon" src={countryFlagUrl(country)} />)
        : (<span style={{ width: '20px', marginRight: '5px' }}> </span>)}
      {!CountryUtil.isDeprecated(country) ? `${countryI18n(country)}` : `${countryI18n(country)} ${ze('(已废弃)', '(Deprecated)')}`}
    </span>;
  };

  const userSceneTagLabelSingular = customUserSceneTagLabel || ze('场景', 'Scene');
  const userSceneTagLabelPlural = customUserSceneTagLabel || ze('场景', 'Scenes');

  const allColumns = [{
    title: userSceneTagLabelSingular,
    width: '200px',
    key: 'userSceneTag',
    dataIndex: 'userSceneTag',
    render: (text, record) => {
      const { userSceneTag } = record;
      return userSceneTag !== DashboardConst.USER_SCENE_TAG_TOTAL_OF_ALL_TAGS
        ? userSceneTag
        : <Tooltip title={ze(`仅统计所有带${userSceneTagLabelSingular}的上报数据。不带${userSceneTagLabelSingular}的上报数据不包含在内。`, `Reports without ${userSceneTagLabelSingular} are not included.`)}>
          <Space size={4}>
            <div>{ ze(`全部${userSceneTagLabelPlural}合计`, `All ${userSceneTagLabelPlural}`) }</div>
            <WrappedTipsIcon />
          </Space>
        </Tooltip>;
    },
    sorter: (a, b) => (a.userSceneTag || '').localeCompare(b.userSceneTag),
  }, ...['EXCP_OVERVIEW.userCrashRate', 'EXCP_OVERVIEW.userANRRate', 'EXCP_OVERVIEW.userErrorRate', 'EXCP_OVERVIEW.deviceOomRate'].map(tab => ({
    title: t(tab),
    width: '200px',
    align: 'right',
    key: tab,
    dataIndex: 'devicePercentage',
    render: (text, record) => {
      if (isMultiDayResult) {
        return multiDayNotSupportedDom;
      }
      const { devicePercentage } = record;
      return `${devicePercentage.toFixed(2)} %`;
    },
    sorter: (a, b) => a.devicePercentage - b.devicePercentage,
  })), ...['EXCP_OVERVIEW.occurNumCrashRate', 'EXCP_OVERVIEW.occurNumANRRate', 'EXCP_OVERVIEW.occurNumErrorRate', 'EXCP_OVERVIEW.numOomRate'].map(tab => ({
    title: t(tab),
    width: '200px',
    align: 'right',
    key: tab,
    dataIndex: 'numPercentage',
    // 和平 + OOM 页面定制需求：默认按次数 OOM 率倒序
    ...(shouldDefaultSortByNumOomRate && tab === 'EXCP_OVERVIEW.numOomRate' ? { defaultSortOrder: 'descend' } : {}),
    render: (text, record) => {
      const { numPercentage } = record;
      return `${numPercentage.toFixed(2)} %`;
    },
    sorter: (a, b) => a.numPercentage - b.numPercentage,
  })), {
    title: t('EXCP_OVERVIEW.sessionCrashRate'),
    width: '200px',
    align: 'right',
    key: 'EXCP_OVERVIEW.sessionCrashRate',
    dataIndex: 'sessionPercentage',
    render: (text, record) => {
      const { sessionPercentage } = record;
      return `${sessionPercentage.toFixed(2)} %`;
    },
    sorter: (a, b) => a.sessionPercentage - b.sessionPercentage,
  }, {
    title: t('EXCP_OVERVIEW.occurNumber'),
    width: '200px',
    align: 'right',
    key: 'EXCP_OVERVIEW.occurNumber',
    dataIndex: 'crashNum',
    // 和平 + OOM 页面时，不再将发生次数作为默认排序（改由次数 OOM 率默认排序）
    ...(shouldDefaultSortByNumOomRate ? {} : { defaultSortOrder: 'descend' }),
    render: (text, record) => formatEnNumber(text),
    sorter: (a, b) => a.crashNum - b.crashNum,
  }, {
    title: t('EXCP_OVERVIEW.sessionNumAffect'),
    width: '200px',
    align: 'right',
    key: 'EXCP_OVERVIEW.sessionNumAffect',
    dataIndex: 'crashSession',
    render: (text, record) => {
      if (isMultiDayResult) {
        return multiDayNotSupportedDom;
      }
      return formatEnNumber(text);
    },
    sorter: (a, b) => a.crashSession - b.crashSession,
  }, {
    title: t('EXCP_OVERVIEW.userNumAffect'),
    width: '200px',
    align: 'right',
    key: 'EXCP_OVERVIEW.userNumAffect',
    dataIndex: 'crashUser',
    render: (text, record) => {
      if (isMultiDayResult) {
        return multiDayNotSupportedDom;
      }
      return formatEnNumber(text);
    },
    sorter: (a, b) => a.crashUser - b.crashUser,
  }, {
    title: ze(`进入${customUserSceneTagLabel || '场景'}次数`, `${customUserSceneTagLabel || 'Scene'} Entered`),
    width: '200px',
    align: 'right',
    key: 'EXCP_OVERVIEW.sessionCount',
    dataIndex: 'accessNum',
    render: (text, record) => formatEnNumber(text),
    sorter: (a, b) => a.accessNum - b.accessNum,
  }, ...(shouldShowAccessPeople ? [{ // 联网用户数列，位于联网设备数列左侧。全量合计行不支持该指标。
    title: ze('联网用户数', 'Active Users'),
    width: '200px',
    align: 'right',
    key: 'accessPeople',
    dataIndex: 'accessPeople',
    render: (text, record) => {
      if (isMultiDayResult) {
        return multiDayNotSupportedDom;
      }
      return formatEnNumber(text);
    },
    sorter: (a, b) => (a.accessPeople || 0) - (b.accessPeople || 0),
  }] : []), {
    title: t('EXCP_OVERVIEW.userNumConnect'),
    width: '200px',
    align: 'right',
    key: 'EXCP_OVERVIEW.userNumConnect',
    dataIndex: 'accessUser',
    render: (text, record) => {
      if (isMultiDayResult) {
        return multiDayNotSupportedDom;
      }
      return formatEnNumber(text);
    },
    sorter: (a, b) => a.accessUser - b.accessUser,
  }];
  // 单独展示列表时TableDom
  const allColumnsTableDom = <Table
    size='small'
    columns={[allColumns[0], ...allColumns.filter(col => tabs.includes(col.key) || col.key === 'accessPeople')]}
    key='allColumns'
    dataSource={filteredCountryStatList}
    showSorterTooltip={false}
    sortDirections={['descend', 'ascend']}
    pagination={{
      showSizeChanger: true,
      defaultPageSize: 5,
      pageSizeOptions: [5, 10, 20, 50, 100],
    }}
    style={{ flex: 1 }}
  />;

  // table组件中defaultSortOrder属性只在第一次渲染时生效，组件挂载时预生成所有指标的TableDOM
  const tablesArr = allColumns.slice(1).map(col => [allColumns[0], { ...col, defaultSortOrder: 'descend' }])
    .map(columns => <Table
      size='small'
      columns={columns}
      key={columns[1].key}
      dataSource={filteredCountryStatList}
      sortDirections={['descend', 'ascend']}
      pagination={filteredCountryStatList.length < 10 ? false : {
        showSizeChanger: false,
        pageSize: 15,
      }}
      style={{ flex: 1 }}
    />);

  function createExcel(data, title) {
    const { utils } = XLSX;
    const ws_name = 'sheet1';
    const wb = utils.book_new();
    /* make worksheet */
    const ws = utils.aoa_to_sheet(data);
    /* Add the worksheet to the workbook */
    XLSX.utils.book_append_sheet(wb, ws, ws_name);
    XLSX.writeFile(wb, title);
  }

  const cardTitle = ze(`按${customUserSceneTagLabel || '场景'}维度统计详情`, `Stats By ${customUserSceneTagLabel || 'Scenes'}`);

  const downloading = (e) => {
    e.stopPropagation();
    const header = [
      userSceneTagLabelSingular,
      t('EXCP_OVERVIEW.userCrashRate'),
      t('EXCP_OVERVIEW.occurNumCrashRate'),
      t('EXCP_OVERVIEW.occurNumber'),
      t('EXCP_OVERVIEW.userNumAffect'),
      ze(`进入${customUserSceneTagLabel || '场景'}次数`, `${customUserSceneTagLabel || 'Scene'} Entered`),
      ...(shouldShowAccessPeople ? [ze('联网用户数', 'Active Users')] : []),
      t('EXCP_OVERVIEW.userNumConnect'),
    ];
    const rows = filteredCountryStatList.map(item => [
      item.userSceneTag !== DashboardConst.USER_SCENE_TAG_TOTAL_OF_ALL_TAGS
        ? item.userSceneTag
        : ze(`全部${userSceneTagLabelPlural}合计`, `All ${userSceneTagLabelPlural}`),
      `${item.devicePercentage.toFixed(2)} %`,
      `${item.numPercentage.toFixed(2)} %`,
      formatEnNumber(item.crashNum),
      formatEnNumber(item.crashUser),
      formatEnNumber(item.accessNum),
      ...(shouldShowAccessPeople
        ? [item.userSceneTag === DashboardConst.USER_SCENE_TAG_TOTAL_OF_ALL_TAGS ? ze('不支持', 'N/A') : formatEnNumber(item.accessPeople)]
        : []),
      formatEnNumber(item.accessUser),
    ]);
    const filename = `${cardTitle}.xlsx`;
    createExcel([header, ...rows], filename);
  };

  return <Spin spinning={loading}>
    <CardHead
      title={cardTitle}
      hideDivider={!isExpanded}
      svgIcon={<CountryIcon />}
    >
      <div style={{ display: 'flex', flexGrow: '1', alignItems: 'center', justifyContent: 'flex-end' }}>
        <UpdateTimeTag updateTime={bigDataRealtimeUpdateMillis} />
        <CsDownloadButton
          style={{ margin: '0px 24px' }}
          onClick={(e) => downloading(e)}
        />

        <CsCollapseButton
          extraStyle={{ width: '14px', height: '14px' }}
          isCollapsed={!isExpanded}
          enableHover={true}
          onClick={(e) => {
            e.stopPropagation();
            if (!isExpanded) {
              reportEvent({
                action: EVENT_ACTIONS.CLICK,
                tp1: '按场景维度统计详情',
              });
            }
            setIsExpanded(!isExpanded);
          }}
        />
      </div>
    </CardHead>
    { isExpanded && <div>
      <Row gutter={10} align='middle' style={{ margin: '20px -5px' }}>
        {
          PlatformUtil.isVmTypeSupported(platformId) && <Col>
            <VmTypeSelect
              value={vmTypeList}
              onChange={(v) => setVmTypeList(v)}
            />
          </Col>
        }
        <Col>
          <VersionHybridSelect
            treeData={versionTree}
            style={{ width: '320px' }}
            value={versionList}
            onChange={v => {
              setVersionList(v);
              latestVersionListRef.current = v;
            }}
          />
        </Col>
        <Col>
          <RangePicker
            style={{ width: '220px' }}
            disabledDate={moment => moment && (moment.isBefore(datePickerMinAvailableMoment) || moment.isAfter(datePickerMaxAvailableMoment))}
            allowClear={false}
            allowEmpty={[false, false]}
            showToday={false}
            placeholder={[ze('日期范围', 'Time Range')]}
            value={[moment(minDate), moment(maxDate)]}
            onChange={(dates) => {
              const [minDate, maxDate] = (dates || [null, null]).map(x => x && x.format('YYYY-MM-DD'));
              setMinDate(minDate);
              setMaxDate(maxDate);
            }}
          />
        </Col>
        <Col>
          <UserSceneTagSelect
            isMultiple={true}
            style={{ width: '320px' }}
            value={userSceneTagList}
            onChangeWhenDropdownClosed={v => setUserSceneTagList(v)}
          />
        </Col>
        <Col><VitalOrCrashSelect /></Col>
        {/** 和平定制需求：按联网设备数过滤，仅和平正式服显示 */}
        { isHepingProdApp && <Col>
          <span style={{ paddingLeft: '4px', color: '#807997'}}>{ze('仅显示联网设备数', 'Min Access Devices')} &gt;= </span>
          <Select
            style={{ width: '90px' }}
            options={MIN_ACCESS_DEVICE_OPTIONS}
            value={minAccessDevice}
            onChange={(v) => setMinAccessDevice(v)}
          />
        </Col> }
        <Col>
          <Button
            type='primary'
            onClick={() => {
              fetchTrend();
              // 和平定制需求：点击查询按钮时，若本地版本与 redux trendVersion 不一致，同步回 redux 使其他组件联动
              if (isHepingApp && typeof updateSearchParams === 'function') {
                const currentLocalVersion = latestVersionListRef.current || [];
                const localVersionStr = currentLocalVersion.length > 0 ? currentLocalVersion.join(',') : '-1';
                const reduxVersionStr = String(initialTrendVersion ?? '-1');
                if (localVersionStr !== reduxVersionStr) {
                  justSyncedVersionRef.current = true;
                  if (!currentLocalVersion.length) {
                    updateSearchParams('trendVersion', -1);
                  } else {
                    updateSearchParams('trendVersion', currentLocalVersion);
                  }
                }
              }
            }}
          >{ t('common.查询') }</Button>
        </Col>
      </Row>
      { isMultipleVersions && <div style={{ margin: '20px 0', width: '100%', color: orange.primary, fontSize: '14px' }}>
        <Space>
          <ExclamationCircleOutlined/>
          <div>{ t('EXCP_OVERVIEW.multipleVersionsWarning')}</div>
        </Space>
      </div> }
      <div className={_style.statByCountry}>
        {tableDom}
      </div>
    </div> }
  </Spin>;
};

StatByUserSceneTag.propTypes = {
  reduxState: PropTypes.object,
  actions: PropTypes.object,
};

const mapStateToProps = state => ({
  reduxState: state,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    // ...productActions,
    // ...appActions,
    // ...globalActions,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(StatByUserSceneTag);
